import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { BOAPI } from '../service/UsersService';
import utils from '../utils/utils';
import BusinessEditModal from './BusinessEditModal';
import FollowUpModal from './FollowUpModal';
import ImportBusinessesModal from './ImportBusinessesModal';
import ImportPendingBusinessesModal from './ImportPendingBusinessesModal';

// תמונה יכולה להיות קישור חיצוני מלא (http/https) ולא רק נתיב /uploads/...
// מקומי - ראו הערה זהה ב-BusinessEditModal.jsx
const fullUrl = (path) => (!path ? null : /^https?:\/\//i.test(path) ? path : `${utils.BaseUrl}${path}`);

const fmtFollowUpDate = (d) => new Date(d).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
// "היום" מחושב בזמן מקומי (לא UTC) - אותה סיבה בדיוק כמו localDateStr ב-AnnouncementsTab.jsx
const isFollowUpDue = (d) => {
  const dt = new Date(d);
  const today = new Date();
  dt.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return dt <= today;
};

const STATUS_TABS = [
  { key: 'all', label: 'הכל' },
  { key: 'active', label: 'פעיל' },
  { key: 'inactive', label: 'מוסתר' },
];

// סטטוס תהליך גיוס - 'confirmed' נגזר תמיד מ-ownerConfirmedAt (לא מהעמודה
// הגולמית leadStatus - ראו effectiveLeadStatus), שאר השלושה נקבעים ידנית
// מ-BusinessEditModal.jsx כל עוד העסק עוד לא אושר בפועל
const LEAD_STATUS_INFO = {
  confirmed:   { label: 'אושר', style: 'statusActive' },
  pending:     { label: 'ממתין', style: 'statusPending' },
  in_progress: { label: 'בתהליך', style: 'statusInProgress' },
  rejected:    { label: 'נדחה', style: 'statusInactive' },
};
const effectiveLeadStatus = (b) => (b.ownerConfirmedAt ? 'confirmed' : (b.leadStatus || 'pending'));

const OWNER_CONFIRM_TABS = [
  { key: 'all', label: 'הכל' },
  { key: 'confirmed', label: 'אושר' },
  { key: 'pending', label: 'ממתין' },
  { key: 'in_progress', label: 'בתהליך' },
  { key: 'rejected', label: 'נדחה' },
];

// טאב "בתי עסק" - הרשימה הפעילה (מה-Businesses, לא הצעות ממתינות - אלה
// בטאב הצעות בתי עסק). "השתקה"/"שחזור" = השבתה/הפעלה (active,
// BOToggleBusinessActive) לפי המוסכמה בפרויקט - לא DELETE אמיתי (ראו
// adminBusinesses.tsx באפליקציה, אותה לוגיקה בדיוק בצד השרת). "מחיקה
// לצמיתות" (deleteBusiness/spBODeleteBusiness) היא כן DELETE אמיתי -
// פעולה נפרדת ומכוונת, חסומה בשרת אם כבר הונפקו לבית העסק עסקאות
const BusinessesTab = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerConfirmFilter, setOwnerConfirmFilter] = useState('all');
  const [toggleBusyId, setToggleBusyId] = useState(null);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deletingBusiness, setDeletingBusiness] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingPending, setImportingPending] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [followUpBusiness, setFollowUpBusiness] = useState(null);

  // קישור אישור התנאים לבעל עסק חדש - מוגש ישירות מהשרת (לא מהאפליקציה/
  // הבק אופיס, ראו server/src/routes/ownerConfirmRouter.ts), אותו דומיין
  // בדיוק כמו כתובת ה-API (utils.BaseUrl) - אין צורך בדומיין נפרד
  const copyConfirmLink = async (business) => {
    const link = `${utils.BaseUrl}/confirm-business?token=${business.confirmToken}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      window.prompt('העתיקו את הקישור:', link);
      return;
    }
    setCopiedId(business.id);
    setTimeout(() => setCopiedId((id) => (id === business.id ? null : id)), 2500);
  };

  // שליחת הקישור בוואטסאפ - אם יש טלפון לבית העסק פותח שיחה ישירות איתו
  // (0501234567 -> 972501234567), אחרת פותח וואטסאפ לבחירת איש קשר
  const shareConfirmLinkWhatsApp = (business) => {
    const link = `${utils.BaseUrl}/confirm-business?token=${business.confirmToken}`;
    const text = `שלום, זה הקישור לאישור ההצטרפות של "${business.name}" למועדון איגוד: ${link}`;
    const digits = String(business.phone || '').replace(/[^0-9]/g, '');
    const intl = digits.startsWith('972') ? digits : digits.startsWith('0') ? `972${digits.slice(1)}` : digits;
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  useEffect(() => {
    loadBusinesses();
  }, []);

  const loadBusinesses = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await BOAPI.fetchBusinesses();
      if (resp?.[0]?.[0]?.code === 0) setBusinesses(resp[1] || []);
      else setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת בתי העסק');
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const toggleActive = async (business) => {
    setToggleBusyId(business.id);
    setError('');
    try {
      const resp = await BOAPI.toggleBusinessActive(business.id);
      if (resp?.[0]?.[0]?.code === 0) {
        const active = !!resp[1]?.[0]?.active;
        setBusinesses((prev) => prev.map((b) => (b.id === business.id ? { ...b, active } : b)));
      } else {
        setError(resp?.[0]?.[0]?.msg || 'העדכון נכשל');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setToggleBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deletingBusiness) return;
    setDeleteBusy(true);
    setError('');
    try {
      const resp = await BOAPI.deleteBusiness(deletingBusiness.id);
      if (resp?.[0]?.[0]?.code === 0) {
        setBusinesses((prev) => prev.filter((b) => b.id !== deletingBusiness.id));
        showSuccess(`"${deletingBusiness.name}" נמחק לצמיתות`);
        setDeletingBusiness(null);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'המחיקה נכשלה');
        setDeletingBusiness(null);
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setDeleteBusy(false);
    }
  };

  const statusFiltered = statusFilter === 'all'
    ? businesses
    : businesses.filter((b) => (statusFilter === 'active' ? b.active : !b.active));

  const ownerConfirmFiltered = ownerConfirmFilter === 'all'
    ? statusFiltered
    : statusFiltered.filter((b) => effectiveLeadStatus(b) === ownerConfirmFilter);

  const filteredBusinesses = search.trim()
    ? ownerConfirmFiltered.filter((b) => {
      const q = search.trim().toLowerCase();
      return [b.name, b.city, b.address, b.ownerName, b.phone].some((v) => (v || '').toLowerCase().includes(q));
    })
    : ownerConfirmFiltered;

  const countByStatus = (key) => (key === 'all' ? businesses.length : businesses.filter((b) => (key === 'active' ? b.active : !b.active)).length);

  const countByOwnerConfirm = (key) => (key === 'all' ? businesses.length : businesses.filter((b) => effectiveLeadStatus(b) === key).length);

  // שמות מוצרים ייחודיים מכל בתי העסק הקיימים - מוצע כ-autocomplete בטופס
  // עריכה/יצירה (datalist), כדי שאדמינים ישתמשו באותו שם ("חולצה") ולא
  // ייצרו כפילויות דומות ("חולצות"/"חולצה כחולה") - ראו BusinessEditModal.jsx
  const existingProductNames = useMemo(() => {
    const set = new Set();
    businesses.forEach((b) => (b.products || []).forEach((p) => set.add(p)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'he'));
  }, [businesses]);

  // מייצא את הרשימה המסוננת כרגע על המסך (חיפוש/טאב סטטוס) - לא בהכרח את כל
  // בתי העסק. עמלת הסליקה (commissionPercent) מותרת כאן כי זה קובץ בק אופיס
  // פנימי בלבד, בניגוד לאפליקציה שלעולם לא מציגה אותה ללקוח (ראו הערה ב-Tables.sql)
  const exportToExcel = () => {
    const data = filteredBusinesses.map((b) => ({
      'שם': b.name,
      'קטגוריות': (b.categoryLabels?.length ? b.categoryLabels : [b.categoryLabel || b.category]).join(', '),
      'אחוז הנחה': b.discountPercent,
      'עמלת סליקה': b.commissionPercent,
      'עיר': b.city || '',
      'כתובת': b.address || '',
      'טלפון': b.phone || '',
      'בעלים': b.ownerName || '',
      'מוצרים': (b.products || []).join(', '),
      'קודים שהונפקו': b.transactionsRequestedCount ?? 0,
      'קודים שמומשו': b.transactionsRedeemedCount ?? 0,
      'סדר תצוגה': b.sortOrder ?? 0,
      'סטטוס': b.active ? 'פעיל' : 'מוסתר',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = Object.keys(data[0] || {}).map(() => ({ wch: 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'בתי עסק');
    XLSX.writeFile(wb, `בתי-עסק-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>בתי עסק</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={s.totalBadge}>{businesses.length} בתי עסק</span>
          <button style={s.exportBtn} onClick={exportToExcel} disabled={filteredBusinesses.length === 0}>
            ⬇ ייצוא לאקסל
          </button>
          <button style={s.importBtn} onClick={() => setImporting(true)}>📥 ייבוא מאקסל</button>
          <button style={s.importBtn} onClick={() => setImportingPending(true)}>📥 ייבוא ממתינים + לינק</button>
          <button style={s.addBtn} onClick={() => setCreating(true)}>+ בית עסק חדש</button>
        </div>
      </div>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}
      {successMsg && <div style={s.successBanner}>✅ {successMsg}</div>}

      <div style={s.tabsRow}>
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            style={{ ...s.tabBtn, ...(statusFilter === t.key ? s.tabBtnActive : {}) }}
            onClick={() => setStatusFilter(t.key)}
          >
            {t.label} <span style={s.tabCount}>{countByStatus(t.key)}</span>
          </button>
        ))}
      </div>

      <div style={s.tabsRow}>
        <span style={s.filterLabel}>אישור בעלים:</span>
        {OWNER_CONFIRM_TABS.map((t) => (
          <button
            key={t.key}
            style={{ ...s.tabBtn, ...(ownerConfirmFilter === t.key ? s.tabBtnActive : {}) }}
            onClick={() => setOwnerConfirmFilter(t.key)}
          >
            {t.label} <span style={s.tabCount}>{countByOwnerConfirm(t.key)}</span>
          </button>
        ))}
      </div>

      <input
        type="text"
        placeholder="חיפוש לפי שם, עיר, כתובת, בעלים או טלפון..."
        style={s.searchInput}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loading ? (
        <div style={s.center}>טוען בתי עסק...</div>
      ) : filteredBusinesses.length === 0 ? (
        <div style={s.emptyState}>
          <div style={{ fontSize: 48 }}>🏪</div>
          <p style={{ color: '#697386', fontWeight: 600 }}>לא נמצאו בתי עסק</p>
        </div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr style={s.headerRow2}>
                <th style={s.th}></th>
                <th style={s.th}>שם</th>
                <th style={s.th}>קטגוריה</th>
                <th style={s.th}>הנחה</th>
                <th style={s.th}>עיר</th>
                <th style={s.th}>בעלים</th>
                <th style={s.th}>קודים</th>
                <th style={s.th}>סדר</th>
                <th style={s.th}>סטטוס</th>
                <th style={s.th}>אישור בעלים</th>
                <th style={s.th}>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {filteredBusinesses.map((b) => (
                <tr key={b.id} style={s.row}>
                  <td style={s.td}>
                    {b.logoUrl ? (
                      <img src={fullUrl(b.logoUrl)} alt="" style={s.logo} />
                    ) : (
                      <div style={s.logoPlaceholder}>{(b.name || '?')[0]}</div>
                    )}
                  </td>
                  <td style={s.td}>
                    <button style={s.nameLink} onClick={() => setEditingBusiness(b)}>{b.name}</button>
                  </td>
                  <td style={s.td}>{(b.categoryLabels?.length ? b.categoryLabels : [b.categoryLabel || b.category]).join(', ')}</td>
                  <td style={s.td}><span style={s.discountBadge}>{b.discountPercent}%</span></td>
                  <td style={s.td}>{b.city || '-'}</td>
                  <td style={s.td}>{b.ownerName || 'ללא בעלים'}</td>
                  <td style={s.td}>{b.transactionsRequestedCount ?? 0} / {b.transactionsRedeemedCount ?? 0}</td>
                  <td style={s.td}>{b.sortOrder ?? 0}</td>
                  <td style={s.td}>
                    <span style={{ ...s.statusBadge, ...(b.active ? s.statusActive : s.statusInactive) }}>
                      {b.active ? 'פעיל' : 'מוסתר'}
                    </span>
                  </td>
                  <td style={s.td}>
                    {b.ownerConfirmedAt ? (
                      <span style={{ ...s.statusBadge, ...s[LEAD_STATUS_INFO.confirmed.style] }}>{LEAD_STATUS_INFO.confirmed.label}</span>
                    ) : (
                      <div style={s.rowActions}>
                        <span style={{ ...s.statusBadge, ...s[LEAD_STATUS_INFO[b.leadStatus || 'pending'].style] }}>
                          {LEAD_STATUS_INFO[b.leadStatus || 'pending'].label}
                        </span>
                        <button
                          style={s.copyLinkBtn}
                          onClick={() => copyConfirmLink(b)}
                        >
                          {copiedId === b.id ? '✓ הועתק' : '🔗 העתקת קישור'}
                        </button>
                        <button style={s.copyLinkBtn} onClick={() => shareConfirmLinkWhatsApp(b)}>
                          📲 וואטסאפ
                        </button>
                        {b.followUpAt ? (
                          <button
                            title={b.followUpNote || ''}
                            style={{ ...s.copyLinkBtn, ...(isFollowUpDue(b.followUpAt) ? s.followUpBadgeDue : s.followUpBadge) }}
                            onClick={() => setFollowUpBusiness(b)}
                          >
                            📅 {fmtFollowUpDate(b.followUpAt)}
                          </button>
                        ) : (
                          <button style={s.copyLinkBtn} onClick={() => setFollowUpBusiness(b)}>📅 מעקב</button>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={s.td}>
                    <div style={s.rowActions}>
                      <button style={s.editBtn} onClick={() => setEditingBusiness(b)}>✏️ עריכה</button>
                      <button
                        style={b.active ? s.deactivateBtn : s.activateBtn}
                        onClick={() => toggleActive(b)}
                        disabled={toggleBusyId === b.id}
                      >
                        {toggleBusyId === b.id ? '...' : b.active ? 'השתקה' : 'שחזור'}
                      </button>
                      <button style={s.deleteBtn} onClick={() => setDeletingBusiness(b)}>
                        🗑️ מחיקה לצמיתות
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BusinessEditModal
        business={editingBusiness}
        existingProductNames={existingProductNames}
        onClose={() => setEditingBusiness(null)}
        onSaved={(updated) => {
          if (!updated) return;
          setBusinesses((prev) => prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)));
          setEditingBusiness(null);
          showSuccess(`"${updated.name}" נשמר בהצלחה`);
        }}
      />

      {creating && (
        <BusinessEditModal
          business={{}}
          existingProductNames={existingProductNames}
          onClose={() => setCreating(false)}
          onSaved={(created) => {
            if (!created) return;
            setBusinesses((prev) => [created, ...prev]);
            setCreating(false);
            showSuccess(`"${created.name}" נוסף בהצלחה`);
          }}
        />
      )}

      <ImportBusinessesModal
        visible={importing}
        onClose={() => setImporting(false)}
        onImported={() => showSuccess('הייבוא הושלם - בדקו את התוצאות בטאב "הצעות בתי עסק" לאישור')}
      />

      <ImportPendingBusinessesModal
        visible={importingPending}
        onClose={() => setImportingPending(false)}
        onImported={() => { showSuccess('הייבוא הושלם - בתי העסק נוספו כממתינים, אפשר לשלוח לכל אחד את הקישור שלו'); loadBusinesses(); }}
      />

      <FollowUpModal
        business={followUpBusiness}
        onClose={() => setFollowUpBusiness(null)}
        onSaved={(updated) => {
          setBusinesses((prev) => prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)));
          setFollowUpBusiness(null);
        }}
      />

      {deletingBusiness && (
        <div style={s.overlay} onClick={() => !deleteBusy && setDeletingBusiness(null)}>
          <div style={s.deleteModalCard} onClick={(e) => e.stopPropagation()}>
            <h3 style={s.deleteModalTitle}>מחיקת בית עסק לצמיתות</h3>
            <p style={s.deleteModalText}>
              למחוק את <strong>"{deletingBusiness.name}"</strong> לצמיתות מהמערכת?
              הפעולה לא ניתנת לביטול. אם כבר הונפקו לבית העסק עסקאות, המחיקה תיחסם -
              אפשר להשתיק אותו במקום.
            </p>
            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setDeletingBusiness(null)} disabled={deleteBusy}>ביטול</button>
              <button style={s.confirmDeleteBtn} onClick={confirmDelete} disabled={deleteBusy}>
                {deleteBusy ? 'מוחק...' : 'מחיקה לצמיתות'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  totalBadge:   { background: '#e8eaf6', color: '#1f6feb', borderRadius: 12, padding: '4px 12px', fontSize: 13, fontWeight: 700 },
  addBtn:       { padding: '9px 16px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  exportBtn:    { padding: '9px 16px', background: '#fff', color: '#1f6feb', border: '1.5px solid #c7d7fb', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  importBtn:    { padding: '9px 16px', background: '#fff', color: '#1f6feb', border: '1.5px solid #c7d7fb', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  center:       { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  successBanner:{ background: '#e8f5e9', color: '#2e7d32', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 },
  emptyState:   { textAlign: 'center', padding: '60px 0' },
  tabsRow:      { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' },
  filterLabel:  { fontSize: 12.5, fontWeight: 700, color: '#8792a2', marginLeft: 2 },
  tabBtn:       { padding: '8px 16px', borderRadius: 999, border: '1.5px solid #e3e8ee', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#697386' },
  tabBtnActive: { background: '#1f6feb', borderColor: '#1f6feb', color: '#fff' },
  tabCount:     { opacity: 0.7, fontWeight: 600 },
  searchInput:  { width: '100%', maxWidth: 360, padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 13, marginBottom: 18, boxSizing: 'border-box' },

  tableWrapper: { overflowX: 'auto', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  table:        { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow2:   { backgroundColor: '#f5f7fa', borderBottom: '2px solid #e3e8ee' },
  th:           { padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#4f566b', fontSize: 13, borderBottom: '2px solid #e3e8ee' },
  row:          { borderBottom: '1px solid #f0f2f5' },
  td:           { padding: '10px 16px', color: '#4f566b', fontSize: 13, textAlign: 'right' },
  logo:         { width: 38, height: 38, borderRadius: 8, objectFit: 'cover' },
  logoPlaceholder: { width: 38, height: 38, borderRadius: 8, background: '#EEF2FF', color: '#1f6feb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15 },
  nameLink:     { background: 'none', border: 'none', cursor: 'pointer', color: '#1f6feb', fontWeight: 700, fontSize: 13, padding: 0, textAlign: 'right' },
  discountBadge:{ display: 'inline-block', background: '#ECFDF5', color: '#16A34A', borderRadius: 10, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  statusBadge:  { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
  statusActive: { background: '#e8f5e9', color: '#2e7d32' },
  statusInactive:{ background: '#fff1f1', color: '#d93838' },
  statusPending: { background: '#fff8e1', color: '#b58105' },
  statusInProgress: { background: '#EEF2FF', color: '#1f6feb' },
  copyLinkBtn:  { padding: '6px 10px', background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 8, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' },
  followUpBadge:   { background: '#EEF2FF', color: '#1f6feb', border: '1.5px solid #c7d7fb' },
  followUpBadgeDue:{ background: '#fff1f1', color: '#c62828', border: '1.5px solid #fbc4c4' },
  rowActions:   { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  editBtn:      { padding: '6px 12px', background: '#EEF2FF', color: '#1f6feb', border: '1px solid #c7d7fb', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  activateBtn:  { padding: '6px 12px', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  deactivateBtn:{ padding: '6px 12px', background: '#fff1f1', color: '#d93838', border: '1px solid #fbc4c4', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  deleteBtn:    { padding: '6px 12px', background: '#fff', color: '#c62828', border: '1px solid #c62828', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100, padding: 16, direction: 'rtl',
  },
  deleteModalCard: {
    width: '100%', maxWidth: 420, background: '#fff', borderRadius: 20,
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)', padding: 24,
  },
  deleteModalTitle: { margin: '0 0 14px', fontSize: 17, fontWeight: 800, color: '#c62828' },
  deleteModalText: { fontSize: 13.5, color: '#4f566b', lineHeight: 1.6, marginBottom: 18 },
  modalActions: { display: 'flex', gap: 10 },
  cancelBtn: { flex: 1, padding: '11px 16px', background: '#fff', color: '#4f566b', border: '1.5px solid #e3e8ee', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  confirmDeleteBtn: { flex: 1, padding: '11px 16px', background: '#c62828', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
};

export default BusinessesTab;
